import { useCallback, useState } from 'react';

/**
 * Uma tentativa de operacao que move dinheiro.
 *
 * O contrato torna `Idempotency-Key` obrigatorio em `createCharge`,
 * `refundCharge`, `recordManualPayment` e `createCheckoutSession`, e o servidor
 * gerado recusa com 400 antes de olhar o corpo quando ele falta. Mas mandar
 * *qualquer* chave nao basta: a chave e o que separa "tentei de novo" de
 * "cobrei de novo".
 *
 * Por isso ela **nao** nasce por montagem, que seria o natural com um `useState`
 * solto. O formulario de emissao continua montado depois de emitir, e a segunda
 * cobranca do dia sairia com a chave da primeira — o servidor devolveria a
 * primeira, calado, e a tela mostraria sucesso sem nada ter sido cobrado.
 * Aqui a chave sobrevive a retentativa (o 403 de reautenticacao repete a mesma,
 * que e o ponto) e e aposentada em `settled()`, o unico instante em que a
 * operacao deixou de existir.
 *
 * Uma tentativa vale por **uma** operacao. Duas operacoes diferentes na mesma
 * tela — pagar com cartao e pagar com Pix, por exemplo — precisam de duas
 * tentativas, e nao de uma compartilhada: a mesma chave com outro corpo autoriza
 * o servidor a responder o pedido de agora com o resultado do anterior.
 */
export interface MoneyAttempt {
  /**
   * Repasse inteiro para o `request` do hook gerado. O cabecalho e montado aqui
   * e em nenhum outro lugar: nenhuma tela precisa saber como ele se escreve, e
   * nenhuma consegue escreve-lo errado.
   */
  readonly request: { readonly headers: Record<'Idempotency-Key', string> };
  /** Encerra a tentativa. A proxima operacao nasce com chave nova. */
  readonly settled: () => void;
}

export function useMoneyAttempt(): MoneyAttempt {
  const [key, setKey] = useState(newIdempotencyKey);
  const settled = useCallback(() => setKey(newIdempotencyKey()), []);
  return { request: { headers: { 'Idempotency-Key': key } }, settled };
}

/** `randomUUID` da 36 caracteres, dentro dos 8..200 que o contrato aceita. */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
